// The client library — every document the client owns, across all engagements.
// Extracted from ClientsPage when the client detail moved from a slide-in panel to a
// full page; the logic (folder chips, upload-via-engagement, Run librarian and its
// 404-tolerant behaviour) is unchanged.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Library, Sparkles, Upload } from 'lucide-react';
import { useToast } from './Toast.js';
import { ApiError } from '../api/client.js';
import { documentsApi, DOCUMENT_FOLDERS, type LibraryResponse } from '../api/documents.js';
import * as crm from '../api/crm.js';
import { useT } from '../i18n/index.js';
import './ClientLibrary.de.js';
import './ClientLibrary.fr.js';

interface Props {
  detail: crm.ClientDetail;
  /**
   * Fires whenever an UNFILTERED library load lands — the page header derives its
   * document count (and the engagement cards their linked-docs counts) from it without
   * a second fetch.
   */
  onLibraryLoaded?: (library: LibraryResponse) => void;
}

export default function ClientLibrary({ detail, onLibraryLoaded }: Props) {
  const toast = useToast();
  const t = useT();
  const clientId = detail.id;

  const [library, setLibrary] = useState<LibraryResponse | null>(null);
  const [libLoading, setLibLoading] = useState(false);
  /** '' = all · '__none' = unclassified (client-side; the server only filters real folders). */
  const [libFolder, setLibFolder] = useState('');
  /** Uploads must arrive THROUGH an engagement (the server links them there on ingest). */
  const [libUploadEng, setLibUploadEng] = useState('');
  const [libUploading, setLibUploading] = useState(false);
  const [librarianBusy, setLibrarianBusy] = useState(false);
  const libFileInput = useRef<HTMLInputElement>(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const loadLibrary = useCallback(async (silent = false) => {
    if (!silent) setLibLoading(true);
    try {
      const res = await documentsApi.library(clientId, {
        folder: libFolder && libFolder !== '__none' ? libFolder : undefined,
      });
      if (mounted.current) {
        setLibrary(res);
        if (!libFolder || libFolder === '__none') onLibraryLoaded?.(res);
      }
    } catch (err) {
      if (!silent) toast('err', (err as Error).message);
    } finally {
      if (mounted.current) setLibLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, libFolder, toast]);

  useEffect(() => { void loadLibrary(); }, [loadLibrary]);

  // Uploads enter through an engagement; default to the client's first one.
  useEffect(() => {
    setLibUploadEng(detail.engagements[0] ? String(detail.engagements[0].id) : '');
  }, [detail]);

  /** Upload into the library — the files land on the client, linked into the chosen engagement. */
  async function uploadToLibrary(files: File[]) {
    if (!files.length) return;
    const engId = Number(libUploadEng);
    if (!engId) { toast('warn', t('Documents enter the library through an engagement — create one first.')); return; }
    setLibUploading(true);
    try {
      const report = await documentsApi.uploadFiles(engId, files);
      const created = report.results.filter((r) => r.status === 'created').length;
      const dupes = report.results.filter((r) => r.status === 'duplicate').length;
      const failed = report.results.filter((r) => r.status === 'failed').length;
      toast(failed ? 'warn' : 'ok', [
        t('{n} added to the library', { n: created }),
        dupes ? t('{n} already on file', { n: dupes }) : '',
        failed ? t('{n} not accepted', { n: failed }) : '',
      ].filter(Boolean).join(' · '));
      await loadLibrary(true);
    } catch (err) {
      toast('err', (err as Error).message);
    } finally {
      setLibUploading(false);
    }
  }

  /** Folder + tags + one-line summary for the whole library, in one pass. */
  async function runLibrarian() {
    setLibrarianBusy(true);
    try {
      const out = await crm.runLibrarian(clientId);
      toast('ok', out.notice ?? t('Librarian finished — {n} document(s) classified', { n: out.classified ?? out.queued ?? t('the') }));
      // The server queues and returns immediately (one serial model call per document on the
      // shared GPU). Stay "Classifying…" while the queue drains, refreshing the table so
      // folders, tags and summaries land row by row. Capped, and advisory: a status blip
      // must not strand the button.
      if (out.queued) {
        const deadline = Date.now() + 30 * 60 * 1000;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 4000));
          let pending = 0;
          try {
            pending = (await documentsApi.librarianStatus()).pending.length;
          } catch { break; }
          await loadLibrary(true);
          if (!pending) { toast('ok', t('Librarian finished — the library is filed.')); break; }
        }
      }
      await loadLibrary(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        toast('warn', t('The librarian is not installed yet — classification arrives with the next release.'));
      } else {
        toast('err', (err as Error).message);
      }
    } finally {
      setLibrarianBusy(false);
    }
  }

  return (
    <>
      {/* ---- Library — the client's documents; engagements only LINK a subset ---- */}
      <div className="flex items-center justify-between mb-8 flex-wrap gap-8">
        <h4 className="section-label" style={{ margin: 0 }}>
          <Library className="icon-sm icon-inline" /> {t('Library')}
          {library && <span className="mono text-xs text-ink-3 ml-8">{library.count}</span>}
        </h4>
        <div className="flex gap-8 items-center">
          <button
            className="btn btn-sm"
            disabled={librarianBusy || !library?.count}
            title={t('Classify every document — folder, tags, one-line summary. Auto-applied, always yours to correct.')}
            onClick={() => void runLibrarian()}
          >
            <Sparkles className="icon-sm" /> {librarianBusy ? t('Classifying…') : t('Run librarian')}
          </button>
          {detail.engagements.length > 1 && (
            <select
              className="form-select"
              style={{ width: 'auto' }}
              title={t('New uploads enter the library through this engagement')}
              value={libUploadEng}
              onChange={(e) => setLibUploadEng(e.target.value)}
            >
              {detail.engagements.map((e) => <option key={e.id} value={e.id}>{t('via {code}', { code: e.code })}</option>)}
            </select>
          )}
          <button
            className="btn btn-sm btn-accent"
            disabled={libUploading || !detail.engagements.length}
            title={detail.engagements.length
              ? t('Add files to the client library')
              : t('Documents enter the library through an engagement — create one first')}
            onClick={() => libFileInput.current?.click()}
          >
            <Upload className="icon-sm" /> {libUploading ? t('Uploading…') : t('Upload')}
          </button>
          <input
            ref={libFileInput} type="file" multiple style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              void uploadToLibrary(files);
            }}
          />
        </div>
      </div>
      <div className="filter-chips mb-8" style={{ flexWrap: 'wrap' }}>
        <button className={`chip${libFolder === '' ? ' active' : ''}`} onClick={() => setLibFolder('')}>{t('All')}</button>
        {DOCUMENT_FOLDERS.map((f) => (
          <button key={f} className={`chip${libFolder === f ? ' active' : ''}`} onClick={() => setLibFolder(f)}>{t(f)}</button>
        ))}
        <button className={`chip${libFolder === '__none' ? ' active' : ''}`} onClick={() => setLibFolder('__none')}>{t('unclassified')}</button>
      </div>
      {libLoading ? (
        <div className="flex justify-center p-24"><div className="spinner" /></div>
      ) : (() => {
        const rows = (library?.rows ?? []).filter((r) => libFolder !== '__none' || !r.folder);
        if (!rows.length) {
          return (
            <div className="empty-state-card mb-24">
              <div className="empty-state-title">
                {library?.count ? t('No documents match this filter') : t('The library is empty')}
              </div>
              <div className="empty-state-desc">
                {library?.count
                  ? t('Pick another folder, or All.')
                  : t('Upload here, or ingest on the Documents page of an engagement — everything lands in this library.')}
              </div>
            </div>
          );
        }
        return (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-compact mb-24">
              <thead>
                <tr>
                  <th style={{ width: 84 }}>{t('Ref')}</th>
                  <th>{t('Document')}</th>
                  <th style={{ width: 120 }}>{t('Folder')}</th>
                  <th style={{ width: 140 }}>{t('Used by')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="src-ref">{r.ref}</td>
                    <td>
                      <span className="text-sm text-ink-1" style={{ overflowWrap: 'anywhere' }}>{r.original_filename}</span>
                      {r.ai_summary && <div className="text-xs text-ink-3">{r.ai_summary}</div>}
                      <div className="flex items-center gap-8 text-xs text-ink-3">
                        {r.status !== 'ready' && <span className={`badge ${r.status === 'failed' ? 'badge-err' : 'badge-plain'}`}>{t(r.status)}</span>}
                        {r.superseded_by_ref && <span className="badge badge-plain mono">→ {r.superseded_by_ref}</span>}
                        {r.cited_by_evidence > 0 && (
                          <span className="mono" title={t('Evidence citations across all engagements')}>{t('{n}× cited', { n: r.cited_by_evidence })}</span>
                        )}
                      </div>
                      {r.tags?.length ? (
                        <div className="flex gap-4 flex-wrap mt-4">
                          {r.tags.map((t) => <span key={t} className="badge badge-plain">{t}</span>)}
                        </div>
                      ) : null}
                    </td>
                    <td className="text-xs text-ink-3">{r.folder ? t(r.folder) : t('unclassified')}</td>
                    <td>
                      <div className="flex gap-4 flex-wrap">
                        {r.engagements.length === 0
                          ? <span className="text-xs text-ink-3">{t('library only')}</span>
                          : r.engagements.map((l) => (
                            <span key={l.id} className="badge badge-plain mono" title={l.origin === 'uploaded_here' ? t('uploaded here') : t('linked from library')}>
                              {l.code}
                            </span>
                          ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}
    </>
  );
}
